import {RefObject, useRef} from 'react';

import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';

/*
 * Types.
 */

interface UseOnSelectionChangeOptions {
  targetRef: RefObject<HTMLElement | null>;
  onSelectionChange: (sel: Selection | null, hasMetaKey: boolean) => void;
  onSelectionDone?: (hasMetaKey: boolean) => void;
}

/*
 * Hook.
 */

export function useOnSelectionChange({
  targetRef,
  onSelectionChange,
  onSelectionDone,
}: UseOnSelectionChangeOptions) {
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const onSelectionDoneRef = useRef(onSelectionDone);
  onSelectionDoneRef.current = onSelectionDone;
  const metaKeyPressed = useRef(false);
  const selection = useRef<Selection | null>(null);

  useInitialEffect(() => {
    const target = targetRef.current;
    if (!target) {
      throw new Error('A valid ref should be provided.');
    }

    const doc = target.ownerDocument;
    if (!doc) {
      throw new Error('A document could not be found for the ref.');
    }

    // Wrap the call to the listener ref into a stable handler.
    const stableListener: EventListener = () => {
      selection.current = doc.getSelection();
      onSelectionChangeRef.current(selection.current, metaKeyPressed.current);
    };
    const onMouse = (e: MouseEvent) => {
      metaKeyPressed.current = e.metaKey;
    };
    const onMouseUp = (e: MouseEvent) => {
      metaKeyPressed.current = e.metaKey;

      onSelectionDoneRef.current?.(e.metaKey);
    };
    doc.addEventListener('selectionchange', stableListener);
    doc.addEventListener('mousedown', onMouse, true);
    doc.addEventListener('mouseup', onMouseUp, true);

    return () => {
      doc.removeEventListener('selectionchange', stableListener);
      doc.removeEventListener('mousedown', onMouse, true);
      doc.removeEventListener('mouseup', onMouseUp, true);
    };
  });
}
